// Guided tour content. Bump TOUR_VERSION after big UI changes to show the tour again to everyone.
export const TOUR_VERSION = 1;

// Each step:
//   route     page the step lives on (the tour navigates there)
//   target    data-tour="..." element to highlight (omit for a centred card)
//   optional  skip silently if the element isn't on screen (e.g. empty lists, role-specific buttons)
//   admin     only shown to admins
export const buildSteps = (user) => {
  const isAdmin = user?.role === 'admin';
  const first = (user?.name || '').split(' ')[0];
  const all = [
    {
      id: 'welcome', route: '/dashboard',
      title: `Welcome to Smart Maji${first ? `, ${first}` : ''} `,
      body: "This quick tour shows you where everything is: payments, meters, accounts and more. It takes about a minute. Use the arrow keys or the buttons below, and you can leave at any time.",
    },
    {
      id: 'nav', route: '/dashboard', target: 'nav',
      title: 'Getting around',
      body: isAdmin
        ? 'Use this menu to move between pages: Dashboard for money collected, Accounts and Manage for meters and users, Customer Value for top customers, and Tenants and Ledger for partner sites and commission.'
        : 'Use this menu to move between pages: Dashboard for money collected, Accounts and Manage for your meters, and Customer Value for your top customers.',
    },
    {
      id: 'month', route: '/dashboard', target: 'month-picker',
      title: 'Choose a month',
      body: 'Everything on the dashboard follows this month. Use the arrows or the list to look back at earlier months.',
    },
    {
      id: 'unfulfilled', route: '/dashboard', target: 'unfulfilled', admin: true, optional: true,
      title: 'Payments that need you',
      body: "When a customer pays but no token could be issued, it appears here. Check the meter number, correct it if needed, then press Retry. The customer gets their token by SMS and WhatsApp.",
    },
    {
      id: 'headline', route: '/dashboard', target: 'headline',
      title: 'Money collected',
      body: isAdmin
        ? 'Total paid for water in the selected month across the whole system, with the year-to-date total on the right.'
        : 'Total paid for water in the selected month on the sites and accounts you look after, with the year-to-date total on the right.',
    },
    {
      id: 'breakdowns', route: '/dashboard', target: 'breakdowns',
      title: 'Who paid',
      body: 'The same month split by tenant site and by account. Longer bars mean more money. Hover over a row to see the number of payments and its share.',
    },
    {
      id: 'ytd', route: '/dashboard', target: 'ytd',
      title: 'The year at a glance',
      body: 'Monthly totals since January. Click any bar to jump the whole dashboard to that month.',
    },
    {
      id: 'meters-stats', route: '/dashboard', target: 'meter-stats', optional: true,
      title: 'Your meters',
      body: 'How many meters you have, and how many are active or inactive.',
    },
    {
      id: 'manage-tabs', route: '/manage', target: 'manage-tabs', optional: true,
      title: 'Manage meters' + (isAdmin ? ' and users' : ''),
      body: isAdmin
        ? 'Switch between the meter list and the user list here.'
        : 'Your meters are listed here.',
    },
    {
      id: 'meters-list', route: '/manage', target: 'meters-list', optional: true,
      title: 'Meter details and tokens',
      body: 'Click a meter to see its payment history, edit its details, attach it to an account, or generate a token or tamper code by hand.',
    },
    {
      id: 'add-meter', route: '/manage', target: 'add-meter', optional: true,
      title: 'Add a meter',
      body: "Register a new meter by its number. Attach it to an account that has a price per unit, so payments can work out how much water to give.",
    },
    {
      id: 'sync', route: '/manage', target: 'sync-meters', optional: true,
      title: 'Sync from the meter system',
      body: 'Brings in meters that were added in the meter supplier’s system, so you don’t have to type them in.',
    },
    {
      id: 'add-user', route: '/manage', target: 'add-user', admin: true, optional: true,
      title: 'Users and access',
      body: 'Add people here. On the Users tab, press Edit on a user to choose which tenant sites they can sign in to and which accounts they can see.',
    },
    {
      id: 'imports', route: '/accounts', target: 'imports', optional: true,
      title: 'Accounts and prices',
      body: 'Accounts group meters and set the price per m³. You can import prices, customers and accounts from Excel instead of entering them one by one.',
    },
    {
      id: 'customer-value', route: '/customer-value', target: 'customer-value', optional: true,
      title: 'Top customers',
      body: 'Meters ranked by how much they have paid, so you can spot your best customers and meters that have gone quiet.',
    },
    {
      id: 'new-tenant', route: '/tenants', target: 'new-tenant', admin: true, optional: true,
      title: 'Tenant sites',
      body: 'Each tenant gets its own address (name.smartwater.co.ke). Every tenant needs at least one user who can sign in.',
    },
    {
      id: 'tenant-payments', route: '/tenants', target: 'tenant-payments', admin: true, optional: true,
      title: 'Payment setup',
      body: "Add a tenant's own M-Pesa paybill or till here, test the keys, then register its URLs with Safaricom. Without one, the tenant collects through the main Majic Water paybill.",
    },
    {
      id: 'ledger', route: '/ledger', target: 'ledger-filters', admin: true, optional: true,
      title: 'Commission ledger',
      body: 'The commission earned from each tenant. Choose a period or a tenant, then use Export CSV for invoicing.',
    },
    {
      id: 'messaging', route: '/messaging', target: 'messaging-tabs', admin: true, optional: true,
      title: 'Messaging',
      body: 'See SMS and WhatsApp messages that failed, then re-send or clear them. You can also change the wording customers receive, and preview or send the monthly email reports.',
    },
    {
      id: 'audit', route: '/audit', target: 'audit-filters', admin: true, optional: true,
      title: 'Audit log',
      body: 'Every change, sign-in, payment callback and background job is recorded here. Choose “Failures only” to see what went wrong, then click an entry to see the details and related events.',
    },
    {
      id: 'help', route: '/dashboard', target: 'tour-help', optional: true,
      title: 'Need a refresher?',
      body: 'Press this ? button any time to take the tour again.',
    },
    {
      id: 'done', route: '/dashboard',
      title: "You're all set ",
      body: 'That’s the tour. Customers pay from the home page (or a tenant’s own address), and their tokens arrive by SMS and WhatsApp straight away.',
    },
  ];
  return all.filter((s) => !s.admin || isAdmin);
};
